import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ScheduleConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postCount: number;
  scheduledAt: string | Date | null;
  platforms: string[];
  onConfirm: () => void;
}

function formatScheduleTime(value: string | Date | null): string {
  if (!value) return 'soon';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return 'soon';
  const day = d.toLocaleDateString(undefined, { weekday: 'short' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} ${time}`;
}

function formatPlatforms(platforms: string[]): string {
  if (platforms.length === 0) return 'your channels';
  if (platforms.length === 1) return platforms[0];
  if (platforms.length === 2) return platforms.join(' + ');
  return `${platforms.slice(0, -1).join(', ')} + ${platforms[platforms.length - 1]}`;
}

/**
 * §21.2 F12 — gate before scheduling approval fires. AlertDialog inherits
 * a11y from the primitive (focus trap, ESC cancels, primary autofocus).
 *
 * Wired from `WorkflowTrackerBody`: when the active step's tools include
 * `schedule_content` AND the step is `awaiting_approval`, the Approve
 * button opens this dialog before firing the approval action.
 */
export function ScheduleConfirmDialog({
  open,
  onOpenChange,
  postCount,
  scheduledAt,
  platforms,
  onConfirm,
}: ScheduleConfirmDialogProps): JSX.Element {
  const noun = postCount === 1 ? 'post' : 'posts';
  const count = postCount > 0 ? postCount : 1;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Schedule {count} {noun}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Posts publish {formatScheduleTime(scheduledAt)} to {formatPlatforms(platforms)}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Schedule</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
